import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, avatarUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { User, Shield, Upload, Camera, AlertTriangle } from "lucide-react";
import { PrivacyDataCard } from "@/components/account/PrivacyDataCard";

const LANGS = [{ value: "en", label: "English" }, { value: "it", label: "Italiano" }];
const FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-slate-900 text-white grid place-items-center"><Icon className="h-4 w-4" /></div>
        <h3 className="font-bold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function MyAccountPage() {
  const { user, setUser, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [p, setP] = useState({ first_name: "", last_name: "", display_name: "", job_title: "", language: "en", date_format: "DD/MM/YYYY" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [setPwForm, setSetPwForm] = useState({ new_password: "", confirm_password: "" });
  const [cap, setCap] = useState(null);
  const [resetToken, setResetToken] = useState("");
  const [delEmail, setDelEmail] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const hasPassword = cap ? cap.has_password : user?.auth_provider === "password";

  useEffect(() => {
    api.get("/auth/password-capability").then((r) => setCap(r.data)).catch(() => {});
  }, [user?.user_id]);

  useEffect(() => {
    api.get("/workspaces")
      .then((r) => {
        const list = r.data.workspaces || [];
        const cur = list.find((w) => w.is_current || w.company_id === user?.company_id);
        if (cur?.name) setWorkspaceName(cur.name);
      })
      .catch(() => {});
  }, [user?.company_id]);

  useEffect(() => {
    if (!user) return;
    setP({
      first_name: user.first_name || "", last_name: user.last_name || "",
      display_name: user.display_name || user.name || "", job_title: user.job_title || "",
      language: user.language || "en", date_format: user.date_format || "DD/MM/YYYY",
    });
  }, [user]);

  const set = (k) => (v) => setP((s) => ({ ...s, [k]: v }));
  const initials = (user?.name || user?.email || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    try { const { data } = await api.put("/me/profile", p); setUser(data); await checkAuth(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await checkAuth(); toast.success("Photo updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    try { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setInitialPassword = async () => {
    if (setPwForm.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (setPwForm.new_password !== setPwForm.confirm_password) { toast.error("Passwords do not match"); return; }
    try {
      await api.post("/auth/set-initial-password", setPwForm);
      setSetPwForm({ new_password: "", confirm_password: "" });
      const r = await api.get("/auth/password-capability"); setCap(r.data);
      toast.success("Password set — you can now sign in with email and password");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const forgot = async () => {
    try { const { data } = await api.post("/auth/forgot-password", { email: user.email }); setResetToken(data.dev_reset_token || ""); toast.success("Reset link generated below"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deactivate = async () => {
    try { await api.post("/me/deactivate"); toast.success("Account deactivated"); await logout(); navigate("/login", { replace: true }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/me", { data: { confirm_email: delEmail } }); toast.success("Account deleted"); await logout(); navigate("/login", { replace: true }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-4xl" data-testid="my-account-page">
      <p className="overline">Personal</p>
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">My Account</h1>
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
        <span>Workspace:</span><Badge variant="outline" data-testid="account-workspace-name">{workspaceName || "…"}</Badge>
        <span>Role:</span><Badge variant="outline" className="capitalize">{user?.role?.replace("_", " ")}</Badge>
      </div>

      <Tabs defaultValue="profile" className="mt-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="account-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="security" data-testid="account-tab-security">Security &amp; privacy</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-4">
          <Card title="Profile" icon={User} testid="account-profile-card">
            <div className="flex items-center gap-4 mb-5">
              <Avatar className="h-16 w-16">
                <AvatarImage src={avatarUrl(user?.picture)} alt={user?.name} />
                <AvatarFallback className="bg-slate-900 text-white text-lg">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="account-avatar-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="account-avatar-upload-btn"><Camera className="h-4 w-4 mr-2" />Change photo</Button>
                <p className="text-xs text-slate-500 mt-1">PNG or JPG, up to 5 MB</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-slate-600">First name</Label><Input value={p.first_name} onChange={(e) => set("first_name")(e.target.value)} className="mt-1" data-testid="account-first-name" /></div>
              <div><Label className="text-xs text-slate-600">Last name</Label><Input value={p.last_name} onChange={(e) => set("last_name")(e.target.value)} className="mt-1" data-testid="account-last-name" /></div>
              <div><Label className="text-xs text-slate-600">Display name</Label><Input value={p.display_name} onChange={(e) => set("display_name")(e.target.value)} className="mt-1" data-testid="account-display-name" /></div>
              <div><Label className="text-xs text-slate-600">Job title</Label><Input value={p.job_title} onChange={(e) => set("job_title")(e.target.value)} className="mt-1" data-testid="account-job-title" /></div>
              <div><Label className="text-xs text-slate-600">Email</Label><Input value={user?.email || ""} disabled className="mt-1 bg-slate-50" data-testid="account-email" /></div>
              <div>
                <Label className="text-xs text-slate-600">Language</Label>
                <Select value={p.language} onValueChange={set("language")}>
                  <SelectTrigger className="mt-1" data-testid="account-language"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Date format</Label>
                <Select value={p.date_format} onValueChange={set("date_format")}>
                  <SelectTrigger className="mt-1" data-testid="account-date-format"><SelectValue /></SelectTrigger>
                  <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-5"><Button onClick={saveProfile} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="account-save-profile">Save changes</Button></div>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6 mt-4">
          <Card title="Password" icon={Shield} testid="account-security-card">
            {hasPassword ? (
              <>
                <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
                  <div><Label className="text-xs text-slate-600">Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} className="mt-1" data-testid="account-current-pw" /></div>
                  <div><Label className="text-xs text-slate-600">New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} className="mt-1" data-testid="account-new-pw" /></div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Button onClick={changePassword} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="account-change-pw-btn">Change password</Button>
                  <Button variant="ghost" onClick={forgot} data-testid="account-forgot-btn">Forgot password?</Button>
                </div>
                {resetToken && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="account-reset-token">
                    Beta: email delivery is mocked. Your one-time reset token: <code className="font-mono break-all">{resetToken}</code>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-4">You sign in with {user?.auth_provider === "google" ? "Google" : user?.auth_provider}. Set a password to also sign in with your email and password. Your {user?.auth_provider === "google" ? "Google" : user?.auth_provider} sign-in keeps working.</p>
                <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
                  <div><Label className="text-xs text-slate-600">New password</Label><Input type="password" value={setPwForm.new_password} onChange={(e) => setSetPwForm((s) => ({ ...s, new_password: e.target.value }))} className="mt-1" data-testid="account-set-new-pw" /></div>
                  <div><Label className="text-xs text-slate-600">Confirm password</Label><Input type="password" value={setPwForm.confirm_password} onChange={(e) => setSetPwForm((s) => ({ ...s, confirm_password: e.target.value }))} className="mt-1" data-testid="account-set-confirm-pw" /></div>
                </div>
                <p className="text-xs text-slate-400 mt-2">At least 8 characters.</p>
                <div className="mt-4"><Button onClick={setInitialPassword} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="account-set-pw-btn">Set password</Button></div>
              </>
            )}
          </Card>

          <PrivacyDataCard />

          <Card title="Danger zone" icon={AlertTriangle} testid="account-danger-card">
            <p className="text-sm text-slate-500 mb-4">Deactivating hides your account but preserves all history (projects, documents, audit trail). Deleting disables your login; business records are always kept.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" data-testid="account-deactivate-btn">Deactivate account</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Deactivate your account?</AlertDialogTitle><AlertDialogDescription>You will be signed out and unable to log in until reactivated by an admin. Nothing is deleted.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={deactivate} data-testid="account-deactivate-confirm">Deactivate</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="account-delete-btn">Delete account</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle><AlertDialogDescription>This disables your login permanently. Projects, documents, reports and audit history are preserved. Type your email to confirm.</AlertDialogDescription></AlertDialogHeader>
                  <Input value={delEmail} onChange={(e) => setDelEmail(e.target.value)} placeholder={user?.email} data-testid="account-delete-email" />
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteAccount} data-testid="account-delete-confirm">Delete account</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
